import React, { useState, useCallback, MouseEvent } from "react";
import {
  ProductType,
  ProductField,
  CreateFieldInput,
  FieldType,
  Flex,
  Box,
  Text,
  Input,
  Checkbox,
  utils,
} from "@thenounproject/lingo-core";
import styled from "styled-components";
import ModalHeader from "../../../../app/_components/ModalHeader";
import ModalBody from "../../../../app/_components/ModalBody";
import ModalFooter from "../../../../app/_components/ModalFooter";
import { useSelectSpace } from "@selectors/entities/spaces";
import useCreateProductType from "@redux/actions/productTypes/useCreateProductType";
import { FIELD_TYPE_REGISTRY } from "../fieldTypeRegistry";
import useNotifications from "@actions/useNotifications";

type Props = {
  productType: ProductType;
  dismissModal?: (e?: MouseEvent | KeyboardEvent) => void;
};

const needsSelectOptions = (fieldType: FieldType): boolean =>
  (fieldType === "select" || fieldType === "multiselect") &&
  !(FIELD_TYPE_REGISTRY[fieldType]?.isManaged ?? false);

const toCreateFieldInput = (field: ProductField, index: number): CreateFieldInput => ({
  name: field.name,
  identifier: field.identifier,
  fieldType: field.fieldType,
  required: false,
  displayOrder: index * 10,
  options: needsSelectOptions(field.fieldType)
    ? field.options?.map(opt => ({ value: opt.value, label: opt.label }))
    : undefined,
  config: field.config ?? {},
});

const FieldRow = styled(Flex).attrs({
  alignItems: "center",
  gap: "12px",
  py: "xs",
  px: "s",
  borderRadius: "4px",
})``;

const FieldTypeBadge = styled(Box)`
  padding: 2px 8px;
  border-radius: 4px;
  background: ${utils.getColor("grayLighter")};
  white-space: nowrap;
`;

const DuplicateProductTypeModal: React.FC<Props> = ({ productType, dismissModal }) => {
  const space = useSelectSpace();
  const [createProductType, { isProcessing, error }] = useCreateProductType();
  const { showNotification } = useNotifications();

  const [name, setName] = useState(`${productType.name} Duplicate`);
  const [selectedFieldIds, setSelectedFieldIds] = useState<Set<number>>(
    () => new Set(productType.fields.map(f => f.id))
  );

  const toggleField = useCallback((fieldId: number) => {
    setSelectedFieldIds(prev => {
      const next = new Set(prev);
      if (next.has(fieldId)) {
        next.delete(fieldId);
      } else {
        next.add(fieldId);
      }
      return next;
    });
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!name.trim()) return;
    const fields = productType.fields
      .filter(
        f => (FIELD_TYPE_REGISTRY[f.fieldType]?.isUnique ?? false) || selectedFieldIds.has(f.id)
      )
      .map((field, index) => toCreateFieldInput(field, index));

    const res = await createProductType({
      spaceId: space.id,
      name: name.trim(),
      description: "",
      displayOrder: "append",
      fields,
    });

    if (res.isSuccess) {
      dismissModal();
    } else if (res.error) {
      showNotification({
        level: "error",
        message: "Failed to duplicate product type",
      });
      console.error("Failed to duplicate product type", res.error);
    }
  }, [
    name,
    productType.fields,
    selectedFieldIds,
    createProductType,
    dismissModal,
    space.id,
    showNotification,
  ]);

  return (
    <>
      <ModalHeader title="Duplicate Product Type" />
      <ModalBody>
        <Box mb="l">
          <Input label="Name" value={name} onChange={e => setName(e.target.value)} autoFocus />
        </Box>
        <Text font="ui.smallBold" mb="xs">
          Duplicating &quot;{productType.name}&quot;. Fields to be added:
        </Text>
        <Flex flexDirection="column" gap="4px">
          {productType.fields.map(field => {
            const isRequired = FIELD_TYPE_REGISTRY[field.fieldType]?.isRequired ?? false;
            const isChecked = isRequired || selectedFieldIds.has(field.id);
            return (
              <FieldRow key={field.id}>
                <Checkbox
                  isSelected={isChecked}
                  disabled={isRequired}
                  onClick={isRequired ? undefined : () => toggleField(field.id)}
                />
                <Box flex="1">
                  <Text>{field.name}</Text>
                </Box>
                <FieldTypeBadge>
                  <Text font="ui.small" color="grayDark">
                    {FIELD_TYPE_REGISTRY[field.fieldType]?.label ?? field.fieldType}
                  </Text>
                </FieldTypeBadge>
                {isRequired && (
                  <Text font="ui.small" color="grayDark">
                    Required
                  </Text>
                )}
              </FieldRow>
            );
          })}
        </Flex>
        {error && (
          <Text color="error" font="ui.small" mt="m">
            {error.message}
          </Text>
        )}
      </ModalBody>
      <ModalFooter
        primary={{
          text: isProcessing ? "Duplicating..." : "Duplicate",
          onClick: handleSubmit,
          disabled: isProcessing || !name.trim(),
        }}
      />
    </>
  );
};

export default DuplicateProductTypeModal;
